import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import { Info, Sparkles } from "lucide-react";
import Button from "../../../../../shared/ui/button/Button";
import { analyzeTherapeuticPlan } from "../../../application/therapeuticPlans";
import { useAiInsightsGate, LockedState } from "../../../../ai_insights";

import { TherapeuticPlan } from "../../../domain/TherapeuticPlan";

interface TherapeuticPlanAIAnalysisProps {
  therapeuticPlan: TherapeuticPlan;
}

interface AIAnalysisData {
  summary?: string;
  path?: string;
  themes?: string[];
  recommendations?: string[];
}

export default function TherapeuticPlanAIAnalysis({ therapeuticPlan }: TherapeuticPlanAIAnalysisProps) {
  const { t } = useTranslation();
  const { adopted: aiInsightsAdopted, loading: adoptionLoading } = useAiInsightsGate();
  const [loadingAI, setLoadingAI] = useState(false);
  const [data, setData] = useState<AIAnalysisData>({
    summary: therapeuticPlan.analysis?.summary ?? "",
    path: therapeuticPlan.analysis?.path ?? "",
    themes: therapeuticPlan.analysis?.themes ?? [],
    recommendations: therapeuticPlan.analysis?.recommendations ?? [],
  });

  const handleAIAnalysis = async () => {
    try {
      setLoadingAI(true);
      const result = await analyzeTherapeuticPlan(therapeuticPlan.id);

      setData({
        summary: result.summary ?? "",
        path: result.path ?? "",
        themes: result.themes ?? [],
        recommendations: result.recommendations ?? [],
      });
    } catch (err) {
      console.error("AI analysis failed:", err);
    } finally {
      setLoadingAI(false);
    }
  };

  if (!adoptionLoading && !aiInsightsAdopted) {
    return <LockedState title={t("aiInsights.locked.therapeuticPlan")} />;
  }

  return (
    <div className="glass-effect glass-border border rounded-3xl p-8 shadow-theme-sm space-y-8">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <h3 className="text-xl font-bold zen-text-gradient">
            {t("therapeuticPlan.aiInsightsTitle")}
          </h3>
        </div>

        <Button
          onClick={handleAIAnalysis}
          disabled={loadingAI}
          className={`
            px-6 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 transition-all shadow-lg shadow-purple-500/20
            ${loadingAI ? "opacity-50 cursor-not-allowed" : "hover:scale-[1.03] active:scale-[0.98]"}
            ai-spectrum-fill
          `}
          startIcon={<Sparkles className={`w-4 h-4 ${loadingAI ? "animate-pulse" : ""}`} />}
        >
          {loadingAI ? t("therapeuticPlan.analyzing") : t("therapeuticPlan.runAnalysis")}
        </Button>
      </div>

      {/* Info Banner */}
      <div className="ai-spectrum-border rounded-xl">
        <div className="rounded-2xl bg-white/40 dark:bg-white/[0.02] backdrop-blur-sm p-5 flex gap-4 items-start border border-white/20 dark:border-white/5">
          <div className="p-2 rounded-lg bg-accent/10 text-accent-text">
            <Info className="w-4 h-4" />
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed font-medium">
            <Trans
              i18nKey="therapeuticPlan.aiInsightsDesc"
              components={{ 1: <strong className="text-accent-text" /> }}
            />
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8">
        {/* SUMMARY */}
        {data.summary && (
          <div className="group relative rounded-2xl border border-gray-100 dark:border-white/5 bg-accent-tint p-8 transition-all hover:shadow-theme-md">
            <h3 className="flex items-center gap-2 text-lg font-bold text-content-primary mb-4">
              {t("therapeuticPlan.highLevelSummary")}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              {data.summary}
            </p>
          </div>
        )}

        {/* PATH */}
        {data.path && (
          <div className="group relative rounded-2xl border border-gray-100 dark:border-white/5 bg-white/40 dark:bg-white/[0.02] p-8 transition-all hover:shadow-theme-md">
            <h3 className="flex items-center gap-2 text-lg font-bold text-content-primary mb-4">
              {t("therapeuticPlan.therapeuticPath")}
            </h3>
            <p className="text-sm text-content-secondary leading-relaxed">
              {data.path}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {/* THEMES */}
          {data.themes && data.themes.length > 0 && (
            <div className="rounded-2xl border border-gray-100 dark:border-white/5 bg-white/40 dark:bg-white/[0.02] p-8">
              <div className="flex items-center gap-2 mb-6">
                <h3 className="text-lg font-bold text-content-primary">
                  {t("therapeuticPlan.recurringThemes")}
                </h3>
              </div>

              <div className="flex flex-wrap gap-2">
                {data.themes.map((theme, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-1.5 text-xs font-bold rounded-xl bg-white dark:bg-white/5 border border-gray-100 dark:border-white/10 text-brand-600 dark:text-brand-400 shadow-theme-xs transition-transform hover:scale-105"
                  >
                    {theme}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* RECOMMENDATIONS */}
          {data.recommendations && data.recommendations.length > 0 && (
            <div className="rounded-2xl border border-gray-100 dark:border-white/5 bg-white/40 dark:bg-white/[0.02] p-8">
              <div className="flex items-center gap-2 mb-6">
                <h3 className="text-lg font-bold text-content-primary">
                  {t("therapeuticPlan.recommendations")}
                </h3>
              </div>

              <ul className="space-y-3">
                {data.recommendations.map((rec, idx) => (
                  <li
                    key={idx}
                    className="group/item flex items-start gap-3 p-4 border border-gray-50/50 dark:border-white/5 rounded-xl bg-white/50 dark:bg-white/[0.01] hover:bg-white transition-all shadow-theme-xs"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-500 mt-1.5 shrink-0 opacity-40 group-hover/item:opacity-100 transition-opacity" />
                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{rec}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );

}
